/**
 * Test Section Content Registry for Analytics
 *
 * Stores content slots from test section components (ContentNewsMixSection, etc.)
 * for use in NTP analytics events. This tracks which articles are displayed in
 * specific layout slots within test sections.
 *
 * Unlike useFetchedContent which tracks all API-fetched content, this module
 * captures the actual slot assignments (e.g., 'row1-article1', 'row2-article2')
 * showing what content appears in each position of a test section layout.
 *
 * Used to populate the `testSectionContent` property in:
 * - NtpPage_Viewed: includes all content slots from the currently displayed test section
 *
 * NOTE: This is only sent on NtpPage_Viewed events, never on NtpMoreNews_Loaded,
 * as test sections are only rendered once on initial page load.
 */

import debug from '../../utils/console'
import type { ArticleCardProps } from '../../components/ArticleCard/ArticleCard.vue'

const logger = debug.withTag('testSectionContent')

/**
 * The stored test section content as a plain object (not a Map).
 * Keys are slot names (e.g., 'row1-article1'), values are the article props.
 */
export type TestSectionContentRecord = Record<string, Partial<ArticleCardProps>>

// Module-level (non-reactive) storage for test section content
let testSectionContent: TestSectionContentRecord | null = null

/**
 * Register test section content from a component's contentSlots Map.
 * Converts the Map to a plain object and stores it.
 *
 * @param slots - Map of slot names to article props (from useContentSlots)
 * @param sectionName - Optional identifier for the section type (for debugging)
 */
export function registerTestSectionContent(
  slots: Map<string, Partial<ArticleCardProps>>,
  sectionName?: string
): void {
  const content: TestSectionContentRecord = {}

  for (const [slotName, articleProps] of slots.entries()) {
    if (articleProps) {
      // Only store slots that have content
      content[slotName] = { ...articleProps }
    }
  }

  // Keep only Quartz and The Inventory articles
  // others will be included in `visibleContent` field for analytics
  const filteredContent: TestSectionContentRecord = {}
  for (const [slotName, articleProps] of Object.entries(content)) {
    if (
      articleProps.publisherName === 'Quartz' ||
      articleProps.publisherName === 'The Inventory'
    ) {
      filteredContent[slotName] = articleProps
    }
  }

  testSectionContent = filteredContent

  logger.log(
    `[testSectionContent] Registered ${Object.keys(content).length} slots${sectionName ? ` from ${sectionName}` : ''}`
  )
}

/**
 * Get the currently registered test section content.
 * Returns the raw object suitable for inclusion in analytics payloads.
 *
 * @returns The test section content record, or null if none registered
 */
export function getTestSectionContent(): TestSectionContentRecord | null {
  return testSectionContent
}

/**
 * Check if test section content has been registered.
 *
 * @returns True if content has been registered, false otherwise
 */
export function hasTestSectionContent(): boolean {
  return (
    testSectionContent !== null && Object.keys(testSectionContent).length > 0
  )
}

/**
 * Clear the registered test section content.
 * Call this on page navigation or when the test section is unmounted.
 */
export function clearTestSectionContent(): void {
  testSectionContent = null
  logger.log('[testSectionContent] Cleared test section content')
}
